<template>
  <main class="container-wide">
    <div v-if="errorResponse">
      <WarningPanel
        :title="'There\'s a problem...'"
        :msg="errorResponse"
        :severe="true"
      />
    </div>
    <div v-else>
      <h1 class="page-title" data-testid="page-title">Here’s Your Package</h1>

      <p class="intro">
        Your housemate <strong>{{ accountCreator }}</strong> has created this
        account for you to join at
        <span class="address">
          {{ address }}
        </span>
      </p>

      <ServiceSummary
        :joining-tenant="true"
        :ppppw="pricePerPersonPerWeek"
        :ppppm="pricePerPersonMonthly"
        :non-discounted-pppw="nonDiscountedPricePerPersonPerWeek"
        :non-discounted-ppppm="nonDiscountedPricePerPersonPerMonthly"
        :discount-description="hasUsedDiscount ? discountDescription : null"
        :tenancy-start-date="tenancyStartDate"
        :tenancy-end-date="tenancyEndDate"
        :first-payment-date="tenancyStartDate"
        :broadband-date="broadbandInstallationDate"
      />

      <button
        class="btn btn--big"
        data-testid="submit-join-next"
        @click="formSubmit"
        id="joining-tenant-summary-conversion"
      >
        Join
      </button>
    </div>
  </main>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import WarningPanel from "@/components/WarningPanel";
import ServiceSummary from "@/components/ServiceSummary";

export default {
  name: "JoiningSummary",
  data() {
    return {
      errorResponse: "",
    };
  },
  computed: {
    ...mapState({
      accountCreator({ accountInformation }) {
        return accountInformation.accountCreator;
      },
      address({ accountInformation }) {
        return accountInformation.address;
      },
      tenancyStartDate({ accountInformation }) {
        return accountInformation.tenancyStartDate;
      },
      tenancyEndDate({ accountInformation }) {
        return accountInformation.tenancyEndDate;
      },
      broadbandInstallationDate({ accountInformation }) {
        return accountInformation.broadbandDate;
      },
      hasUsedDiscount({ accountInformation }) {
        return accountInformation.usedDiscount;
      },
      discountDescription({ accountInformation }) {
        return accountInformation.discountDescription;
      },
      discount: (state) => state.discount,
    }),
    ...mapGetters([
      "pricePerPerson",
      "nonDiscountedPricePerPerson",
      "pricePerPersonPerMonth",
      "nonDiscountedPricePerPersonPerMonth",
    ]),
    pricePerPersonPerWeek() {
      return (this.$store.getters.pricePerPerson / 100).toFixed(2);
    },
    pricePerPersonMonthly() {
      let amountInPounds = this.pricePerPersonPerMonth / 100;

      return Math.ceil(amountInPounds * 100) / 100;
    },
    nonDiscountedPricePerPersonPerWeek() {
      return this.pricePerPerson !== this.nonDiscountedPricePerPerson
        ? (this.nonDiscountedPricePerPerson / 100).toFixed(2)
        : null;
    },
    nonDiscountedPricePerPersonPerMonthly() {
      let amountInPounds = this.nonDiscountedPricePerPersonPerMonth / 100;

      let amount = Math.ceil(amountInPounds * 100) / 100;

      return this.hasUsedDiscount ? amount : null;
    },
  },
  methods: {
    ...mapMutations({
      showLoadingPanel: "SHOW_LOADING_PANEL",
      hideLoadingPanel: "HIDE_LOADING_PANEL",
      setAccountInformation: "SET_ACCOUNT_INFORMATION",
    }),
    ...mapActions(["getAccountInformation"]),
    formSubmit() {
      this.trackGoogleEvent(
        "Click Let's Go",
        "Joining Housemate",
        "Join account",
      );

      this.$router.push("/joining-tenant/personal-details");
    },
  },
  created() {
    // ensure back btn is hidden
    this.$store.commit("HIDE_BACK_BTN");

    this.trackGoogleEvent(
      "Land on Join landing page",
      "Joining Housemate",
      "Land on join page",
    );

    // check for referral code in url and validate against endpoint
    const guid = this.$route.query.guid;

    if (guid) {
      this.showLoadingPanel();

      this.getAccountInformation(guid)
        .then(({ data }) => {
          this.setAccountInformation({
            guid,
            ...data,
          });

          // track successful join code
          this.trackGoogleEvent(
            "Customer clicks through to STB via a valid join link",
            "Campaign & Channel",
            "Valid join link",
          );
        })
        .catch((error) => {
          if (!error.response) {
            this.errorResponse = "Network error accessing the STB API";
          } else {
            if (error.response.status === 410) {
              // if the account is already 100% complete
              this.$router.push({
                name: "house-already-complete",
                params: {
                  address: error.response.data.address,
                },
              });
            } else {
              this.errorResponse = error.response.data.messages[0];

              // track successful join code
              this.trackGoogleEvent(
                "Customer clicks through to UniHomes via a invalid join link",
                "Campaign & Channel",
                "Invalid join link",
              );
            }
          }
        })
        .finally(() => {
          this.hideLoadingPanel();
        });
    } else {
      this.errorResponse = "Cannot detect guid";
    }
  },
  components: {
    ServiceSummary,
    WarningPanel,
  },
};
</script>

<style lang="scss" scoped>
.page-title {
  margin-bottom: 0;
}

.intro {
  font-size: 16px;
  line-height: 1.8;
  margin: 0 0 $spacing-medium 0;
  color: $grey-dark;
  text-align: center;

  strong {
    color: $brand-dark-blue;
  }

  .address {
    font-weight: $font-bold;
    display: block;
  }
}
</style>
